import React from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import classNames from 'classnames';
import { format } from 'date-fns';
import { CountryFlag } from 'project/country-flag';
import eventBus from 'general/js/event-bus';
import ComponentsFactory from 'foundation/components-factory';
import app, { EVENT_DEVICE_TYPE_CHANGED } from 'general/js/app';
import { SpriteSvg } from 'project/sprite-svg';
import QualDrawTablePlayer from './qual-draw-table-player';
import QualDrawSearchInput from './qual-draw-search-input';
import QualDrawSelect from './qual-draw-select';
import { getChoices, getPlayerStatusSuffix } from './action-helper';
import { filteringStrategies, sortByTeeTime } from './filtering-strategies';
import {
    R1_TEE_TIME,
    R2_TEE_TIME,
    qualificationTypes
} from './enums';
import { isNullOrUndefined } from '../../../general/js/general-helpers';
import { getQualDrawCellClasses } from './classname-helper';
import historyController from '../../../general/js/history-controller';

const QUAL_DRAW_ID = 'qual-draw-container';
class QualDrawContainer extends React.Component {
    constructor(props) {
        super(props);

        this.rootRef = React.createRef();
        this.arrowLeftRef = React.createRef();
        this.arrowRightRef = React.createRef();
        this.searchQuery = '';
        this.r1TeeTime = false;
        this.r2TeeTime = false;
        this.isFinalQualificationDraw = false;
        this.lastScroll = 0;
        this.chainStartUrl = window.location.pathname;
        this.chainStartHash = window.location.hash;
        this.qsObj = qs.parse(window.location.search, { ignoreQueryPrefix: true });

        this.state = {
            deviceTypeByViewport: '',
            searchAnimationState: false,
            selectedCourse: this.qsObj.course || this.props.locationNameToFilterBy || '',
            applySearchPlayers: false,
        };

        this.initState();
    }

    componentDidMount() {
        ComponentsFactory.getInstance().init(this.rootRef.current);
        eventBus.addListener(EVENT_DEVICE_TYPE_CHANGED, this.changedRenderedViews);
    }

    componentWillUnmount() {
        ComponentsFactory.getInstance().destroy(this.rootRef.current);
        eventBus.removeListener(EVENT_DEVICE_TYPE_CHANGED, this.changedRenderedViews);
    }

    initState = () => {
        historyController.register(QUAL_DRAW_ID, () => {});

        this.qualCellClasses = getQualDrawCellClasses(this.props.locationNameToFilterBy);
        this.qualDrawCellClass = this.qualCellClasses.qualDrawCellClass;
        this.playerNameCellClass = this.qualCellClasses.playerNameCellClass;
        this.playerCellClass = this.qualCellClasses.playerCellClass;

        this.optionItems = getChoices(this.props.players, this.qsObj);

        if (this.props.options.type === qualificationTypes.FINAL) {
            this.isFinalQualificationDraw = true;
        }

        this.props.players.forEach((item) => {
            if (item[R1_TEE_TIME]) {
                this.r1TeeTime = true;
            }

            if (item[R2_TEE_TIME]) {
                this.r2TeeTime = true;
            }
        });
    }


    changedRenderedViews = () => {
        this.setState({ deviceTypeByViewport: app.deviceTypeByViewport });
    }

    renderStartHole = (startHole) => {
        if (startHole !== 10) return '';
        return '*';
    }

    renderMatchNumber = (item) => {
        if (this.isFinalQualificationDraw) return null;

        if (item.isScoringCompleted) {
            return (
                <div className={`${this.qualDrawCellClass} qual-draw__cell--game-number`}>
                    <div className='qual-draw__cell-game-number-content'>
                        <span className='qual-draw__cell-game-number'>{item.r1MatchNo}</span>
                    </div>
                </div>
            );
        }

        return (
            <div className={`${this.qualDrawCellClass} qual-draw__cell--game-number`}>
                <div className='qual-draw__cell-game-number-content'>
                    <span className='qual-draw__cell-game-number'>{item.r1MatchNo}</span>
                </div>
            </div>
        );
    }

    renderField = (field, title, data, startHole) => {
        field = field || '';
        title = title || false;
        data = data || null;
        startHole = startHole || null;

        const roundNoIdentifier = this.isFinalQualificationDraw ? 'r1' : '';

        if (this.r1TeeTime && field === R1_TEE_TIME) {
            if (title) {
                return (
                    <div className={`${this.qualDrawCellClass} qual-draw__cell--title qual-draw__cell--r1`}>
                        <div className="qual-draw__cell-title">tee time {roundNoIdentifier}</div>
                    </div>
                );
            }
            return (
                <div className={`${this.qualDrawCellClass} qual-draw__cell--r1`}>
                    <div className="qual-draw__cell-tee-time-content">
                        <span className="qual-draw__cell-tee-time">{data !== null ? data : '-'}</span>
                        <span className="qual-draw__cell-tee-time-asterisk">{this.renderStartHole(startHole)}</span>
                    </div>
                </div>
            );
        }

        if (this.r2TeeTime && field === R2_TEE_TIME) {
            if (title) {
                return (
                    <div className={`${this.qualDrawCellClass} qual-draw__cell--title qual-draw__cell--r2`}>
                        <div className="qual-draw__cell-title">tee time r2</div>
                    </div>
                );
            }
            return (
                <div className={`${this.qualDrawCellClass} qual-draw__cell--r2`}>
                    <div className="qual-draw__cell-tee-time-content">
                        <span className="qual-draw__cell-tee-time">{data !== null ? data : '-'}</span>
                        <span className="qual-draw__cell-tee-time-asterisk">{this.renderStartHole(startHole)}</span>
                    </div>
                </div>
            );
        }

        return null;
    };

    renderItem = () => {
        return (
            this.items.map((item) => {
                const teeTimeR1 = item.r1TeeTime && format(item.r1TeeTime.replace('Z', ''), 'h:mm a');
                const teeTimeR2 = item.r2TeeTime && format(item.r2TeeTime.replace('Z', ''), 'h:mm a');
                const playerId = '' +
                    item.firstName + '-' +
                    item.lastName + '-' +
                    item.nationality + '-' +
                    item.clubName + '-' +
                    item.locationName;
                return (
                    <div className="qual-draw__row" key={playerId}>
                        <div className={`${this.qualDrawCellClass} ${this.playerCellClass}`}>
                            <div className="qual-draw__cell-player">
                                <CountryFlag
                                    childCss={'qual-draw__cell-player-flag'}
                                    image={item.countryFlag}
                                    title={item.countryName}
                                />
                                <div className={`${this.playerNameCellClass}`}>
                                    {item.lastName.toUpperCase()}, {item.firstName} {getPlayerStatusSuffix(item.playerStatus)}
                                </div>
                            </div>
                        </div>
                        <div className={`${this.qualDrawCellClass} qual-draw__cell--club-name`}>
                            {item.clubName}
                        </div>
                        {
                            !this.props.locationNameToFilterBy &&
                            <div className={`${this.qualDrawCellClass} qual-draw__cell--course`}>
                                {
                                    item.sitecoreData &&
                                    item.sitecoreData.pageUrl &&
                                    <a
                                        className="qual-draw__cell-course-link"
                                        href={item.sitecoreData.pageUrl}
                                        data-dc-component="modal-spa-trigger"
                                    >
                                        {item.sitecoreData.name}
                                    </a>
                                }
                            </div>
                        }
                        {this.renderMatchNumber(item)}
                        {this.renderField(R1_TEE_TIME, false, teeTimeR1, item.r1StartHole)}
                        {this.renderField(R2_TEE_TIME, false, teeTimeR2, item.r2StartHole)}
                    </div>
                );
            })
        );
    }

    onScrollLeftElement = (event) => {
        const scrollLeft = event.target.scrollLeft;
        const scrollWidth = event.target.scrollWidth;
        const offsetWidth = event.target.offsetWidth;

        if (scrollLeft === scrollWidth - offsetWidth) {
            this.arrowRightRef.current.classList.add('is-animation');
        } else if (scrollLeft === 0) {
            this.arrowLeftRef.current.classList.remove('is-animation');
        } else if (scrollLeft > this.lastScroll) {
            this.arrowLeftRef.current.classList.add('is-animation');
        } else if (scrollLeft < this.lastScroll) {
            this.arrowRightRef.current.classList.remove('is-animation');
        }

        this.lastScroll = event.target.scrollLeft;
    }

    handleChangeSelect = (value) => {
        let url = '';

        if (value === '' || isNullOrUndefined(value)) {
            url = `${this.chainStartUrl}${this.chainStartHash}`;
        } else {
            url = `${this.chainStartUrl}?course=${encodeURIComponent(value)}${this.chainStartHash}`;
        }

        historyController.pushState(QUAL_DRAW_ID, null, url);

        this.setState({ selectedCourse: value });
    }

    getDefaultCallback = (term) => {
        this.searchQuery = term.trim().toLowerCase().split(/\s+/);

        this.setState({ applySearchPlayers: term !== '' });
    }

    filteredStateInit = (data, filteringType) => {
        const currentStrategy = filteringStrategies[filteringType];

        const props = {
            players: data,
            selectValue: this.state.selectedCourse,
            searchQuery: this.searchQuery
        };

        return currentStrategy.filteringFunc(props);
    }

    processData(players) {
        if (!Array.isArray(players)) return [];

        let processedPlayers = players;

        if (this.state.selectedCourse) {
            processedPlayers = this.filteredStateInit(processedPlayers, 'selectCourse');
            processedPlayers = sortByTeeTime(processedPlayers, this.r1TeeTime, this.r2TeeTime);
        }

        if (this.state.applySearchPlayers) {
            processedPlayers = this.filteredStateInit(processedPlayers, 'searchPlayers');
            processedPlayers = sortByTeeTime(processedPlayers, this.r1TeeTime, this.r2TeeTime);
        }

        return processedPlayers;
    }

    render() {
        this.items = this.processData(this.props.players);
        const stateCss = classNames({
            'is-search-active': this.state.searchAnimationState
        });

        return (
            <React.Fragment>
                <div className={`qual-draw__controls-container ${stateCss}`} ref={this.rootRef}>
                    <div className="qual-draw__controls-wrapper">
                        <div className="qual-draw__controls-col-1">
                            {this.optionItems.length > 1 &&
                                <QualDrawSelect
                                    options={this.optionItems}
                                    onChange={this.handleChangeSelect}
                                    defaultValue={this.props.locationNameToFilterBy}
                                    isDisabled={!!this.props.locationNameToFilterBy}
                                />
                            }
                            <QualDrawSearchInput
                                deviceTypeByViewport={app.deviceTypeByViewport}
                                defaultCallback={this.getDefaultCallback}
                            />
                        </div>
                        <div className="qual-draw__controls-col-2">
                            <div className="qual-draw__inline-info">
                                {this.props.options.infoInlineRichText &&
                             <div dangerouslySetInnerHTML={{ __html: this.props.options.infoInlineRichText }} />}
                            </div>
                            <a
                                className="qual-draw__draw-info"
                                href={this.props.options.infoPopupUrl}
                                data-dc-component="modal-trigger"
                            >
                                <SpriteSvg
                                    childCss={'qual-draw__draw-info-icon'}
                                    iconName={'i'}
                                    width={20}
                                    height={20}
                                />
                            </a>
                        </div>
                    </div>
                </div>
                {this.props.options.infoInlineRichText &&
                             <div className="qual-draw__inline-info-mobile" dangerouslySetInnerHTML={{ __html: this.props.options.infoInlineRichText }} />}
                {
                    this.items.length > 0 ?
                        <div className="qual-draw__table-container">

                            <QualDrawTablePlayer items={this.items} qualDrawCellClass={this.qualDrawCellClass}/>

                            <div className="qual-draw__arrows">
                                <div
                                    className="qual-draw__arrows-icon-wrapper qual-draw__arrows-icon-wrapper--left"
                                    ref={this.arrowLeftRef}
                                >
                                    <SpriteSvg
                                        childCss={'qual-draw__arrows-icon'}
                                        iconName={'arrow-down'}
                                        width={10}
                                        height={10}
                                    />
                                </div>
                                <div
                                    className="qual-draw__arrows-icon-wrapper qual-draw__arrows-icon-wrapper--right"
                                    ref={this.arrowRightRef}
                                >
                                    <SpriteSvg
                                        childCss={'qual-draw__arrows-icon'}
                                        iconName={'arrow-down'}
                                        width={10}
                                        height={10}
                                    />
                                </div>
                            </div>

                            <div
                                className="qual-draw__table-wrapper"
                                onScroll={this.onScrollLeftElement}
                            >
                                <div className="qual-draw__table">
                                    <div className="qual-draw__row">
                                        <div className={`${this.qualDrawCellClass} qual-draw__cell--title qual-draw__cell--player`}>
                                            <div className="qual-draw__cell-title">
                                                <div className="qual-draw__cell-title-content">player</div>
                                            </div>
                                        </div>
                                        <div
                                            className={`${this.qualDrawCellClass} qual-draw__cell--title qual-draw__cell--club-name`}>
                                            <div className="qual-draw__cell-title show-desktop">club/attachment</div>
                                            <div className="qual-draw__cell-title show-mobile">attachment</div>
                                        </div>
                                        {!this.props.locationNameToFilterBy && <div className={`${this.qualDrawCellClass} qual-draw__cell--title qual-draw__cell--course`}>
                                            <div className="qual-draw__cell-title">course</div>
                                        </div>}
                                        {this.isFinalQualificationDraw ? null :
                                            <div className={`${this.qualDrawCellClass} qual-draw__cell--title qual-draw__cell--game-number`}>
                                                <div className="qual-draw__cell-title">game</div>
                                            </div>}
                                        {this.renderField(R1_TEE_TIME, true)}
                                        {this.renderField(R2_TEE_TIME, true)}
                                    </div>

                                    {this.renderItem()}
                                </div>
                            </div>
                        </div> : <div className="qual-draw__no-results">
                            <div>No results.</div>
                            <div>Please try making your search a little wider</div>
                        </div>
                }
            </React.Fragment>
        );
    }
}

QualDrawContainer.propTypes = {
    options: PropTypes.object,
    players: PropTypes.array
};

export default QualDrawContainer;
